import { FC, memo, ReactNode, useMemo } from 'react';
import { ConditionalWrapper } from 'components/Shared/ConditionalWrapper';
import * as Styled from './styles';
import { RAINBOW_COLORS } from 'constants/design/rainbow-colors';
import AvatarPlaceholder from 'assets/images/user-image.png';
import type { TPlacementCorner, TShapeType, TSizeType } from 'interfaces';

export type TAvatarTextProps = {
  className?: string;
  size?: TSizeType;
  shape?: TShapeType;
  avatar?: string | null;
  text: string | ReactNode;
  secondaryText?: string | ReactNode;
  isSecondaryTextBlur?: boolean;
  fallbackInitials?: boolean;
  onClick?: Function;
  showAvatar?: boolean;
  reverseText?: boolean;
  hideText?: boolean;
  fixedWhite?: boolean;
  plainAvatar?: boolean;
  isTextHighlighted?: boolean;
  badge?: {
    placement: TPlacementCorner;
    icon: ReactNode;
  };
};

export const AvatarText: FC<TAvatarTextProps> = memo(
  ({
    className,
    size = 'medium',
    shape = 'circle',
    avatar,
    text,
    secondaryText,
    isSecondaryTextBlur = false,
    fallbackInitials = false,
    onClick,
    showAvatar = true,
    hideText = false,
    reverseText = false,
    fixedWhite = false,
    plainAvatar = false,
    isTextHighlighted = false,
    badge
  }) => {
    const initials = useMemo(() => {
      if (!fallbackInitials) {
        return {};
      }
      if (typeof text === 'string') {
        return {
          text: plainAvatar
            ? text.substring(0, 1).toUpperCase()
            : text.substring(0, 2).toUpperCase(),
          color: plainAvatar
            ? `var(--v3-black)`
            : RAINBOW_COLORS[Math.floor(Math.random() * RAINBOW_COLORS.length)]
        };
      }
      return {};
    }, [fallbackInitials, text, secondaryText]);

    const showInitials = useMemo(
      () => fallbackInitials && !avatar,
      [fallbackInitials, avatar]
    );

    const isOneLine = useMemo(
      () => size === 'small' || !secondaryText,
      [size, secondaryText]
    );

    return (
      <Styled.AvatarTextWrapper
        className={className}
        $size={size}
        $shape={shape}
        $oneLine={isOneLine}
        $clickable={!!onClick}
        $fixedWhite={fixedWhite}
        $plainAvatar={plainAvatar}
        onClick={() => {
          onClick?.();
        }}
      >
        {showAvatar ? (
          <ConditionalWrapper
            condition={!!badge}
            wrapper={children => (
              <Styled.Badge
                $placement={badge?.placement}
                count={badge?.icon}
                $size={size}
              >
                {children}
              </Styled.Badge>
            )}
          >
            <>
              {showInitials && (
                <Styled.AvatarFallback $bgColor={initials.color}>
                  {initials.text}
                </Styled.AvatarFallback>
              )}
              {!showInitials && (
                <Styled.Avatar
                  src={avatar ?? AvatarPlaceholder}
                  wrapperStyle={{
                    gridRowStart: 1,
                    gridRowEnd: isOneLine ? 2 : 3
                  }}
                  preview={false}
                  alt={text as string}
                />
              )}
            </>
          </ConditionalWrapper>
        ) : (
          <Styled.NoAvatarFallback />
        )}
        {!hideText &&
          (reverseText ? (
            <>
              <Styled.SecondaryText $isTextBlur={isSecondaryTextBlur}>
                {text}
              </Styled.SecondaryText>
              {!isOneLine && (
                <Styled.PrimaryText>{secondaryText}</Styled.PrimaryText>
              )}
            </>
          ) : (
            <>
              <Styled.PrimaryText $isTextHighlighted={isTextHighlighted}>
                {text}
              </Styled.PrimaryText>
              {!isOneLine && (
                <Styled.SecondaryText $isTextBlur={isSecondaryTextBlur}>
                  {secondaryText}
                </Styled.SecondaryText>
              )}
            </>
          ))}
      </Styled.AvatarTextWrapper>
    );
  }
);
